import { Box, Grid, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import { FlexBox } from 'components/Containers/FlexBox';
import type { ProductionLoaderFuelType } from 'types/production.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  unitSystem: string;
}

export const LoaderEventsFuelConsumption = ({
  enableDarkTheme,
  unitSystem,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: {
      allFleet: { loaderEvents },
    },
  } = useAppSelector((state) => state.productionReducer);

  const hasChartData = loaderEvents.length > 0;

  const colors = ['#A776FF', '#FF0068', '#FF9C2C', '#165BAA'];
  const formatKey = (str: string) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const eventsFuelChartData: ProductionLoaderFuelType[] = loaderEvents
    .map((item, index) => {
      return {
        value: Math.round(item.current_fuel_consumption_l * 100) / 100,
        color: colors[index],
        name: formatKey(item.event_type),
      };
    })
    .sort((a, b) => b.value - a.value);

  return (
    <Grid item lg={6}>
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          padding: 2,
          minHeight: '300px',
          maxHeight: '300px',
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {t('average_loader_events_fuel_consumption')}
        </Typography>
        <Box
          sx={{
            minHeight: '200px',
            maxHeight: '200px',
          }}
        >
          <FlexBox
            sx={{
              maxWidth: 'inherit',
              height: '230px',
              paddingTop: '20px',
              marginRight: '10px',
              paddingBottom: '10px',
            }}
          >
            {hasChartData ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={eventsFuelChartData}
                  margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
                  barSize={30}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={palette.cartesianGrid}
                    vertical={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: palette.textSecondary }}
                    label={{
                      value: `Fuel Consumed(${
                        unitSystem === 'imperial' ? 'gallons' : 'liters'
                      })`,
                      fontSize: 10,
                      position: 'bottom',
                      offset: 0,
                    }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{
                      fill: palette.textSecondary,
                      fontSize: '11px',
                    }}
                  />
                  <Tooltip
                    cursor={{
                      fill: enableDarkTheme
                        ? `${palette.primary}26`
                        : `${palette.primary}1a`,
                    }}
                    wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                    contentStyle={{
                      backgroundColor: enableDarkTheme
                        ? 'rgba(0,0,0, 0.6)'
                        : 'rgba(255, 255, 255, 0.6)',
                      borderRadius: '4px',
                    }}
                    itemStyle={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.wheat
                        : ThemePalette.typography.black,
                    }}
                  />
                  <Bar dataKey="value">
                    {eventsFuelChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                sx={{
                  fontSize: '11px',
                }}
              >
                {t('no_data_available_currently')}
              </Typography>
            )}
          </FlexBox>
        </Box>
      </Box>
    </Grid>
  );
};

export default LoaderEventsFuelConsumption;
